import {
  LABEL_MAX_PER_JOB,
  LABEL_MAX_PER_VARIANT,
  LABEL_MAX_VARIANTS,
  type LabelRequest,
  type LabelSheet,
  type ProductSummary,
  type ProductVariantSummary,
  type VariantLabel,
} from '@optiflow/types';
import type { LabelPresetId } from './labelPresets';

/** One variant on the label builder, as the reader sees it; the quantity is the field's raw text so a half-typed value is not lost. */
export interface LabelLine {
  variantId: string;
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  quantity: string;
}

/** What an entry point hands the builder through navigation state; never the address, so nothing lands in history or logs. */
export interface LabelBuilderState {
  lines: LabelLine[];
  /** Present when returning from the print page, so the job comes back exactly as it was left. */
  options?: { presetId: LabelPresetId; showPrice: boolean; showSku: boolean };
}

/** What the builder hands the print page: the request, which the print page reads from the cache the preview already filled. */
export interface LabelPrintState {
  request: LabelRequest;
  presetId: LabelPresetId;
  /** The builder as it was, handed back when the reader returns to it. */
  builder: LabelBuilderState;
}

export const LABEL_QUANTITY_PRESETS = [1, 5, 10] as const;

export function labelLine(
  variant: ProductVariantSummary,
  product: Pick<ProductSummary, 'name' | 'nameAr'>,
  quantity = 1,
): LabelLine {
  return {
    variantId: variant.id,
    sku: variant.sku,
    productName: product.name,
    productNameAr: product.nameAr,
    variantName: variant.variantName,
    quantity: String(quantity),
  };
}

/** Adds lines, keeping the first of any variant already on the list so a second scan never doubles a row. */
export function mergeLines(current: LabelLine[], added: LabelLine[]): LabelLine[] {
  const seen = new Set(current.map((line) => line.variantId));
  const fresh = added.filter((line) => {
    if (seen.has(line.variantId)) return false;
    seen.add(line.variantId);
    return true;
  });
  return [...current, ...fresh];
}

/** The quantity a field holds, or null when it is not a whole number from 1 to the per-variant limit. */
export function parseQuantity(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value >= 1 && value <= LABEL_MAX_PER_VARIANT ? value : null;
}

export type LabelJobProblem = 'EMPTY' | 'QUANTITY' | 'TOO_MANY_LABELS' | 'TOO_MANY_VARIANTS';

/** Why the lines cannot be printed yet, or null when they can; the server checks the same bounds again. */
export function jobProblem(lines: LabelLine[]): LabelJobProblem | null {
  if (lines.length === 0) return 'EMPTY';
  if (lines.length > LABEL_MAX_VARIANTS) return 'TOO_MANY_VARIANTS';
  const quantities = lines.map((line) => parseQuantity(line.quantity));
  if (quantities.some((quantity) => quantity === null)) return 'QUANTITY';
  const total = quantities.reduce<number>((sum, quantity) => sum + (quantity ?? 0), 0);
  return total > LABEL_MAX_PER_JOB ? 'TOO_MANY_LABELS' : null;
}

/** The total of every valid quantity, for the running count. */
export function totalLabels(lines: LabelLine[]): number {
  return lines.reduce((sum, line) => sum + (parseQuantity(line.quantity) ?? 0), 0);
}

export function labelRequest(
  lines: LabelLine[],
  options: { showPrice: boolean; showSku: boolean },
): LabelRequest | null {
  if (jobProblem(lines) !== null) return null;
  return {
    items: lines.map((line) => ({
      variantId: line.variantId,
      quantity: parseQuantity(line.quantity) ?? 1,
    })),
    showPrice: options.showPrice,
    showSku: options.showSku,
  };
}

/** Each label repeated as many times as asked, in order. */
export function expandLabels(sheet: LabelSheet): VariantLabel[] {
  return sheet.labels.flatMap((label) => Array.from({ length: label.quantity }, () => label));
}
